import { StatusType, StatusStyle, ThemeMode } from "./statusTypes";
import { STATUS_PALETTE } from "./statusPalette";

/** CSS custom property names for main/foreground - see status-variables.css */
export const STATUS_CSS_VAR: Record<StatusType, string> = {
  pass: "--status-pass",
  fail: "--status-fail",
  failed_resolved: "--status-failed-resolved",
  due: "--status-due",
  overdue: "--status-overdue",
  missed: "--status-missed",
  incomplete: "--status-incomplete",
  pending: "--status-pending",
  skipped: "--status-skipped",
  not_due: "--status-pending",
  complete: "--status-pass",
  excluded: "--status-excluded",
};

/** CSS custom property names for chip/badge backgrounds - see status-variables.css */
export const STATUS_CSS_VAR_BG: Record<StatusType, string> = {
  pass: "--status-pass-bg",
  fail: "--status-fail-bg",
  failed_resolved: "--status-failed-resolved-bg",
  due: "--status-due-bg",
  overdue: "--status-overdue-bg",
  missed: "--status-missed-bg",
  incomplete: "--status-incomplete-bg",
  pending: "--status-pending-bg",
  skipped: "--status-skipped-bg",
  not_due: "--status-pending-bg",
  complete: "--status-pass-bg",
  excluded: "--status-excluded-bg",
};

function paletteMain(status: StatusType, mode: ThemeMode): string {
  return STATUS_PALETTE[status]?.[mode]?.main ?? STATUS_PALETTE.pending[mode].main;
}

function paletteBackground(status: StatusType, mode: ThemeMode): string {
  return STATUS_PALETTE[status]?.[mode]?.background ?? STATUS_PALETTE.pending[mode].background;
}

function paletteIcon(status: StatusType, mode: ThemeMode): string {
  const entry = STATUS_PALETTE[status]?.[mode];
  return entry?.icon ?? entry?.main ?? STATUS_PALETTE.pending[mode].main;
}

/**
 * Returns the spec main/foreground hex for a status in the requested theme mode.
 * Uses STATUS_PALETTE directly so swatches, icons, and chips match the hex tables
 * (not legacy MUI theme.palette success/warning/error colors).
 */
export function getStatusMainColor(status: StatusType, mode: ThemeMode): string {
  return paletteMain(status, mode);
}

/** Icon color when it differs from chip text (e.g. Skipped light theme). */
export function getStatusIconColor(status: StatusType, mode: ThemeMode): string {
  return paletteIcon(status, mode);
}

function getStatusBackgroundColor(status: StatusType, mode: ThemeMode): string {
  return paletteBackground(status, mode);
}

export function getStatusStyle(status: StatusType, mode: ThemeMode): StatusStyle {
  const mainColor = getStatusMainColor(status, mode);
  return {
    backgroundColor: getStatusBackgroundColor(status, mode),
    color: mainColor,
    borderColor: mainColor,
  };
}

/**
 * A dot/swatch is a filled shape with no paired chip background behind it, so it should use the
 * icon color when the palette defines one (today, only "skipped" does - its `main` is calibrated
 * as TEXT sitting on the "skipped" chip's own background, not as a standalone fill color; see
 * statusPalette.ts's comment on that entry). Every other status has no separate `icon` value, so
 * this is identical to getStatusMainColor for them.
 */
export function getStatusDotColor(status: StatusType, mode: ThemeMode): string {
  return getStatusIconColor(status, mode);
}
